import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MetaHead from "./MetaHead";
import Footer from "./Footer";
import SocialHandles from "./SocialHandles";
import { trackPageVisit } from "../utils/analytics";

type AppCard = {
  id: string;
  nameKey: string;
  url: string;
  descriptionKey: string;
  highlightsTitleKey: string;
  highlightKeys: string[];
  badgeClassName: string;
};

const apps: AppCard[] = [
  {
    id: "1",
    nameKey: "otherApps.dailyMintName",
    url: "https://dailymint.netlify.app/",
    descriptionKey: "otherApps.dailyMintDescription",
    highlightsTitleKey: "otherApps.dailyMintHighlightsTitle",
    highlightKeys: [
      "otherApps.dailyMintHighlight1",
      "otherApps.dailyMintHighlight2",
      "otherApps.dailyMintHighlight3",
    ],
    badgeClassName:
      "bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-emerald-300/60",
  },
  {
    id: "2",
    nameKey: "otherApps.subSenseName",
    url: "https://subsense-main.netlify.app/",
    descriptionKey: "otherApps.subSenseDescription",
    highlightsTitleKey: "otherApps.subSenseHighlightsTitle",
    highlightKeys: [
      "otherApps.subSenseHighlight1",
      "otherApps.subSenseHighlight2",
      "otherApps.subSenseHighlight3",
    ],
    badgeClassName:
      "bg-gradient-to-r from-sky-500 to-cyan-500 text-white border-sky-300/60",
  },
];

const OtherApps: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    trackPageVisit("other-apps");
  }, []);

  return (
    <>
      <MetaHead
        title={t("otherApps.title")}
        description={t("otherApps.description") as string}
        keywords={t("otherApps.keywords") as string}
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 animate-fade-in">
        <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                  Streak Spark
                </span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link
                  to="/"
                  className="text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-300"
                >
                  {t("otherApps.home")}
                </Link>
                <Link
                  to="/app"
                  className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-2 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  {t("otherApps.openApp")}
                </Link>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-4 py-8 text-center">
          <div className="mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-600 rounded-2xl shadow-glow mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14-7l-7 7-7-7m14 14l-7-7-7 7"
                />
              </svg>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary-600 via-accent-600 to-secondary-600 bg-clip-text text-transparent mb-4 leading-tight">
              {t("otherApps.heroTitle")}
            </h1>

            <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-300 max-w-3xl mx-auto leading-relaxed">
              {t("otherApps.heroDescription")}
            </p>
          </div>

          <section
            className="grid md:grid-cols-2 gap-6"
            aria-label={t("otherApps.sectionLabel") as string}
          >
            {apps.map((app) => (
              <article
                key={app.id}
                className="glass-card rounded-2xl p-7 text-left border border-neutral-200/70 dark:border-neutral-700/70 hover:shadow-medium transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center justify-between gap-4 mb-4">
                  <span
                    className={`inline-flex items-center justify-center min-w-8 h-8 px-2 rounded-full text-xs font-bold border ${app.badgeClassName}`}
                  >
                    {app.id}
                  </span>
                  <span className="text-xs uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">
                    {t("otherApps.byRdx")}
                  </span>
                </div>

                <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-3">
                  {t(app.nameKey)}
                </h2>

                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
                  {t(app.descriptionKey)}
                </p>

                <div className="mb-6 rounded-xl border border-neutral-200/70 dark:border-neutral-700/70 bg-white/60 dark:bg-neutral-900/50 p-4">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-neutral-700 dark:text-neutral-200 mb-3">
                    {t(app.highlightsTitleKey)}
                  </h3>
                  <ul className="space-y-2">
                    {app.highlightKeys.map((highlightKey) => (
                      <li
                        key={highlightKey}
                        className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed"
                      >
                        <span className="mr-2 text-primary-600 dark:text-primary-400">
                          •
                        </span>
                        {t(highlightKey)}
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-2.5 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  <span>{t("otherApps.visitApp")}</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 3h7m0 0v7m0-7L10 14m-4 7h15a2 2 0 002-2V9"
                    />
                  </svg>
                </a>
              </article>
            ))}
          </section>
        </div>

        <SocialHandles />
        <Footer />
      </div>
    </>
  );
};

export default OtherApps;
